import { getProfileDetail } from "@carnet/api/backoffice";
import type { ProfileResource } from "@carnet/api/backoffice";

// Accès synchrone à la donnée profil (ref réactive, partagée via la clé "profile").
// À utiliser quand on ne peut pas faire de top-level await (ex : composant enfant
// téléporté dans une modale, où un async setup exigerait un Suspense).
export function useProfileData(options?: { watchSession?: boolean; lazy?: boolean }) {
  const { loggedIn } = useUserSession();
  return useAsyncData<ProfileResource | null>(
    "profile",
    async () => {
      if (!loggedIn.value) return null;
      const response = await getProfileDetail({ composable: "$fetch", path: { locale: appLocale() } });
      return response?.data ?? null;
    },
    // `dedupe: "defer"` : un refresh concurrent (login + refreshNuxtData du dashboard, etc.)
    // réutilise la requête en vol au lieu d'en lancer une seconde en parallèle.
    // `watch: [loggedIn]` re-fetche après login/logout (sinon le `null` du boot hors connexion
    // resterait figé → currentMemberId null → isCreator faux), mais UNIQUEMENT porté par
    // l'appelant racine (app.vue, toujours monté) : si chaque consommateur le portait, un
    // changement de session ferait feu sur N watchers de la même clé → perfect-debounce
    // enchaîne un leading + un trailing → deux fetch profil consécutifs.
    {
      getCachedData: cachedDataUnlessRefresh,
      dedupe: "defer",
      lazy: options?.lazy ?? true,
      ...(options?.watchSession ? { watch: [loggedIn] } : {}),
    }
  );
}

export async function useProfile() {
  const localePath = useLocalePath();
  const asyncData = useProfileData();
  await asyncData;

  async function logout() {
    await navigateTo(localePath({ name: "logout" }));
  }

  return { data: asyncData.data, logout };
}
